import React, { useEffect, useMemo, useState } from 'react'
import { useUnit } from 'effector-react'
import dayjs from 'dayjs'
import { useKPIList } from './hooks'
import { CCardBody } from '@coreui/react-pro'
import {
  Card,
  Divider,
  Flex,
  Progress,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { KPI } from './types'
import { KPIFilters } from '../atoms/KPIFilters'
import { $userProfile } from 'src/features/user'
import { rolesObj } from 'src/libs/role'
import { DatePickerProps } from 'antd/es/date-picker'
import {
  BarChartOutlined,
  RiseOutlined,
  StarOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import {
  getPercentageColor,
  getPercentageStatus,
} from '../../shared/percentageStatus'

const { Text } = Typography

const kpiTag = (val: number) => {
  return (
    <Tag
      color={getPercentageStatus(val)}
      style={{
        fontFamily: 'monospace',
        minWidth: 62,
        textAlign: 'center',
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {val.toFixed(2)}
    </Tag>
  )
}

const processedCell = (val: number) => {
  const pct = Math.min(val, 200)
  return (
    <Flex align="center" gap={10}>
      <span
        style={{
          fontFamily: 'monospace',
          width: 72,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {val.toFixed(2)}%
      </span>
      <Progress
        percent={pct / 2}
        showInfo={false}
        strokeColor={getPercentageColor(val)}
        trailColor="rgba(128,128,128,0.15)"
        style={{ flex: 1, minWidth: 80, margin: 0 }}
        size={['100%', 5]}
      />
    </Flex>
  )
}

const speedCell = (val: number) => {
  return (
    <Flex align="center" gap={10}>
      <span style={{ fontFamily: 'monospace', width: 52, flexShrink: 0 }}>
        {val.toFixed(2)}%
      </span>
      <Progress
        percent={val}
        showInfo={false}
        strokeColor={getPercentageColor(val)}
        trailColor="rgba(128,128,128,0.15)"
        style={{ flex: 1, minWidth: 80, margin: 0 }}
        size={['100%', 5]}
      />
    </Flex>
  )
}

const evaluationCell = (val: number) => (
  <Flex align="center" gap={6}>
    <StarOutlined style={{ color: '#f59e0b', fontSize: 12 }} />
    <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{val}</span>
  </Flex>
)

const kpiCell = (val: number) => {
  return (
    <Flex align="center" gap={10}>
      {kpiTag(val)}
      <Progress
        percent={Math.min(val, 200) / 2}
        showInfo={false}
        strokeColor={getPercentageColor(val)}
        trailColor="rgba(128,128,128,0.15)"
        style={{ flex: 1, minWidth: 60, margin: 0 }}
        size={['100%', 5]}
      />
    </Flex>
  )
}

const nameCell = (val: string) => (
  <Text strong style={{ fontSize: 12 }}>
    {val}
  </Text>
)

const SummaryCards: React.FC<{ kpiList: KPI[] }> = ({ kpiList }) => {
  if (!kpiList.length) return null

  const avgKpi = (
    kpiList.reduce((s, r) => s + r.kpiPercent, 0) / kpiList.length
  ).toFixed(2)
  const topKpi = [...kpiList].sort((a, b) => b.kpiPercent - a.kpiPercent)[0]
  const aboveThreshold = kpiList.filter((r) => r.kpiPercent >= 86).length
  const avgSpeed = (
    kpiList.reduce((s, r) => s + r.quickRecordsPercent, 0) / kpiList.length
  ).toFixed(1)

  return (
    <Flex gap={12} style={{ marginBottom: 16 }} wrap="wrap">
      <Card size="small" style={{ flex: 1, minWidth: 150 }}>
        <Statistic
          title={
            <Tooltip title="Средний итоговый KPI по всем менеджерам (% Processed × 0.5 + % Speed × 0.3 + Evaluation × 0.2)">
              <BarChartOutlined style={{ color: '#3b82f6' }} /> Avg KPI
            </Tooltip>
          }
          value={avgKpi}
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: getPercentageColor(Number(avgKpi)),
          }}
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 150 }}>
        <Statistic
          title={
            <Tooltip title="Максимальный KPI среди менеджеров за выбранный период">
              <TrophyOutlined style={{ color: '#f59e0b' }} /> Top KPI
            </Tooltip>
          }
          value={topKpi?.kpiPercent.toFixed(2)}
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: getPercentageColor(topKpi?.kpiPercent ?? 0),
          }}
          suffix={
            <Text type="secondary" style={{ fontSize: 11 }}>
              {topKpi?.username}
            </Text>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 150 }}>
        <Statistic
          title={
            <Tooltip title="Количество менеджеров с KPI ≥ 70 из общего числа">
              <RiseOutlined style={{ color: '#22c55e' }} /> KPI ≥ 70
            </Tooltip>
          }
          value={aboveThreshold}
          suffix={
            <Text type="secondary" style={{ fontSize: 11 }}>
              / {kpiList.length}
            </Text>
          }
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: '#22c55e',
          }}
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 150 }}>
        <Statistic
          title={
            <Tooltip title="Средний % заявок (выводы + внутренние), обработанных менее чем за 15 минут">
              <ThunderboltOutlined style={{ color: '#fbbf24' }} /> Avg Speed
            </Tooltip>
          }
          value={avgSpeed}
          suffix="%"
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: getPercentageColor(Number(avgSpeed)),
          }}
        />
      </Card>
    </Flex>
  )
}

export const KPIList: React.FC = () => {
  const { kpiList, loading, onChangeKPIList, totalCountOfKPI } = useKPIList()
  const userProfile = useUnit($userProfile)
  const hasFullAccess = useMemo(
    () =>
      userProfile.roles.includes(rolesObj.ceo) ||
      userProfile.roles.includes(rolesObj.cfo),
    [userProfile],
  )

  const [filterUserIds, setFilterUserIds] = useState<string[] | undefined>()
  const [datePeriod, setDatePeriod] = useState<DatePickerProps['value']>(() =>
    dayjs().subtract(1, 'month'),
  )
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    }
  }

  useEffect(() => {
    const userIds = hasFullAccess ? filterUserIds : [userProfile.id]
    onChangeKPIList({
      userIds,
      periodName: datePeriod?.format('YYYY-MM'),
      limit: pageSize,
      page,
    })
  }, [hasFullAccess, userProfile.id, filterUserIds, datePeriod, pageSize, page])

  const handleFiltersChange = ({
    userIds,
    datePeriod: nextDatePeriod,
  }: {
    userIds?: string[]
    datePeriod?: DatePickerProps['value']
  }) => {
    setDatePeriod(nextDatePeriod)
    setFilterUserIds(userIds)
    setPage(1)
  }

  const columns: ColumnsType<KPI> = [
    {
      title: 'Manager',
      dataIndex: 'username',
      fixed: 'left',
      width: 160,
      sorter: (a, b) => a.username.localeCompare(b.username),
      defaultSortOrder: 'ascend',
      render: nameCell,
    },
    {
      title: (
        <Tooltip title="Процент выполнения относительно ожидаемого объёма обработанных заявок отдела">
          % Processed
        </Tooltip>
      ),
      dataIndex: 'allExecPercent',
      width: 240,
      sorter: (a, b) => a.allExecPercent - b.allExecPercent,
      render: processedCell,
    },
    {
      title: (
        <Tooltip title="Процент заявок (выводы + внутренние), обработанных менее чем за 15 минут">
          % Speed
        </Tooltip>
      ),
      dataIndex: 'quickRecordsPercent',
      width: 220,
      sorter: (a, b) => a.quickRecordsPercent - b.quickRecordsPercent,
      render: speedCell,
    },
    {
      title: (
        <Tooltip title="Оценка руководителя (по умолчанию 100)">
          Evaluation
        </Tooltip>
      ),
      dataIndex: 'evaluation',
      width: 120,
      align: 'left',
      sorter: (a, b) => a.evaluation - b.evaluation,
      render: evaluationCell,
    },
    {
      title: (
        <Tooltip title="Итоговый KPI = % Processed × 0.5 + % Speed × 0.3 + Evaluation × 0.2">
          KPI
        </Tooltip>
      ),
      dataIndex: 'kpiPercent',
      width: 200,
      sorter: (a, b) => a.kpiPercent - b.kpiPercent,
      defaultSortOrder: 'descend',
      render: kpiCell,
    },
  ]

  return (
    <CCardBody>
      <KPIFilters
        classname="mb-3"
        value={{ userIds: filterUserIds, datePeriod }}
        onChange={handleFiltersChange}
        hasFullAccess={hasFullAccess}
      />

      <SummaryCards kpiList={kpiList} />

      <Table
        dataSource={kpiList}
        bordered
        size="small"
        scroll={{ x: 800 }}
        loading={loading}
        rowKey="userId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          pageSize: pageSize,
          total: totalCountOfKPI,
        }}
        rowClassName={(row) => (row.kpiPercent >= 86 ? 'top-row' : '')}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      />

      <Flex gap={16} style={{ marginTop: 12 }} align="center" wrap="wrap">
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}
        >
          Процент выполнения:
        </Text>
        <Tag color="error">&lt; 70%</Tag>
        <Tag color="warning">70–85.99%</Tag>
        <Tag color="success">≥ 86%</Tag>
        <Divider type="vertical" />
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}
        >
          Speed:
        </Text>
        <Tag color="error">&lt; 70%</Tag>
        <Tag color="warning">70–85.99%</Tag>
        <Tag color="success">≥ 86%</Tag>
      </Flex>
    </CCardBody>
  )
}
